import { X } from '@phosphor-icons/react';
import { ClipLoader } from 'react-spinners';
import {
  AccentButton,
  NeutralButton,
} from '../../../../../../../shared/Buttons';
import { MaintenanceRecordWithDisplayName } from '../../../../../../../types/apiTypes';
import { RecordFields } from './RecordFields';
import { ReminderFields } from './ReminderFields';
import { useMaintenanceForm } from './useMaintenanceForm';
import { useMaintenanceMutations } from './useMaintenanceMutations';
import { MaintenanceModalProps } from './types';

/**
 * One modal for all four maintenance flows, selected by the
 * `{kind: 'record' | 'reminder'} × {mode: 'create' | 'edit'}` discriminated
 * union. It replaces AddMaintenanceRecordModal, EditMaintenanceRecordModal,
 * AddMaintenanceReminderModal and EditMaintenanceReminderModal with identical
 * behavior: `useMaintenanceForm` owns form state + edit prefill, the field
 * components render the per-kind inputs, and `useMaintenanceMutations` submits
 * the exact same payloads/toasts/refresh. The legacy 3× `screenView` JSX is
 * replaced by responsive Tailwind (mobile base, `md:` ≥600px).
 */
export function MaintenanceModal(props: MaintenanceModalProps) {
  const { isOpen, onClose, onRecordChange, kind, mode } = props;

  const selectedAsset = props.selectedAsset;
  const existingRecord =
    kind === 'record' && mode === 'edit' ? props.existingRecord : undefined;
  const existingReminder =
    kind === 'reminder' && mode === 'edit' ? props.existingReminder : undefined;
  const maintenanceRecords: MaintenanceRecordWithDisplayName[] =
    kind === 'reminder' ? props.maintenanceRecords : [];
  const assetList = kind === 'reminder' ? props.assetList : undefined;

  const {
    form,
    fileName,
    setFileName,
    file,
    setFile,
    isNewFile,
    handleFileChange,
    fetchedRecords,
    setFetchedRecords,
    loadingRecords,
    pickedAsset,
  } = useMaintenanceForm({ kind, mode, existingRecord, existingReminder });

  const { loading, submit } = useMaintenanceMutations({
    kind,
    mode,
    selectedAsset,
    existingRecord,
    existingReminder,
    pickedAsset,
    file,
    fileName,
    isNewFile,
    onClose,
    onRecordChange,
    resetForm: form.reset,
    clearFile: () => {
      setFileName(null);
      setFile(null);
    },
  });

  if (!isOpen) return null;

  const isRecord = kind === 'record';
  const title = `${mode === 'create' ? 'Add' : 'Edit'} Maintenance ${
    isRecord ? 'Record' : 'Reminder'
  }`;
  const submitLabel = mode === 'create' ? 'Add' : 'Update';

  const handleClose = () => {
    if (!isRecord) {
      setFetchedRecords([]);
      form.reset();
    }
    onClose();
  };

  const boxClass = isRecord
    ? 'h-[640px] max-h-[640px] w-[95%] max-w-[520px] rounded-[4px] bg-white md:h-[700px] md:max-h-[700px] md:w-full'
    : 'h-[600px] max-h-[600px] w-[90%] max-w-[361px] rounded-[4px] bg-white md:h-[700px] md:max-h-[700px] md:w-full md:max-w-[520px]';

  const formClass = isRecord
    ? 'flex h-full max-h-[504px] flex-col gap-3 border-[1px] border-bordergray p-2 md:max-h-[564px] md:p-6'
    : 'flex h-full max-h-[464px] flex-col gap-3 border-[1px] border-bordergray p-6 md:max-h-[564px] md:gap-6';

  const footerClass = isRecord
    ? 'flex justify-between space-x-2 p-4 md:space-x-0'
    : 'flex justify-between space-x-3 p-4';

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-50">
      <div className={boxClass}>
        <div className="flex h-[56px] w-full flex-row items-center justify-between p-4 align-middle">
          <h2 className="text-[16px] font-[600] text-primary">{title}</h2>
          <X
            className="hover:cursor-pointer hover:opacity-80"
            onClick={handleClose}
            weight="regular"
            size={24}
          />
        </div>
        <form className={formClass} onSubmit={form.handleSubmit(submit)}>
          {isRecord ? (
            <RecordFields
              form={form}
              mode={mode}
              fileName={fileName}
              handleFileChange={handleFileChange}
            />
          ) : (
            <ReminderFields
              form={form}
              selectedAsset={selectedAsset}
              maintenanceRecords={maintenanceRecords}
              assetList={assetList}
              fetchedRecords={fetchedRecords}
              loadingRecords={loadingRecords}
            />
          )}
        </form>
        <div className={footerClass}>
          <NeutralButton
            onClick={handleClose}
            addClass="rounded-full w-[238px] h-[48px]"
          >
            Cancel
          </NeutralButton>
          <AccentButton
            submit={true}
            onClick={form.handleSubmit(submit)}
            addClass="rounded-full hover:bg-yellow-600 w-[238px] h-[48px]"
          >
            {!loading ? (
              submitLabel
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loading}
                size={20}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="h-full w-full justify-center"
              />
            )}
          </AccentButton>
        </div>
      </div>
    </div>
  );
}

export default MaintenanceModal;
